import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { fetchRepairReference, fetchLinkedCases, REF_CATEGORIES, REF_MEASUREMENT_TYPES, REF_SOURCE_TYPES, REF_VERIFICATION, DEVICE_FAMILIES, RESULT_OPTIONS, measurementTypeLabel } from '../lib/repair-kb-client';
import type { RepairReference, RefMedia, RefAnnotation, RefMeasurement, LinkedCase } from '../lib/repair-kb-client';
import { ChevronRight, Loader, AlertTriangle, Image, X } from '../lib/icons';

// ── Helpers ───────────────────────────────────────────────────────────────────

function deviceLabel(v: string) { return DEVICE_FAMILIES.find(d => d.value === v)?.label ?? v; }
function categoryLabel(v: string) { return REF_CATEGORIES.find(c => c.value === v)?.label ?? v; }
function sourceLabel(v: string) { return REF_SOURCE_TYPES.find(s => s.value === v)?.label ?? v; }
function verificationLabel(v: string) { return REF_VERIFICATION.find(r => r.value === v)?.label ?? v; }

function verificationColor(v: string) {
  switch (v) {
    case 'verified':  return 'text-green-400 bg-green-900/30 border-green-800';
    case 'confirmed': return 'text-blue-400 bg-blue-900/30 border-blue-800';
    default:          return 'text-yellow-400 bg-yellow-900/30 border-yellow-800';
  }
}

function formatDate(iso: string | null) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function prose(text: string) {
  return text.split('\n').filter(Boolean).map((line, i) => (
    <p key={i} className="text-gray-300 leading-relaxed mb-2 last:mb-0">{line}</p>
  ));
}

// ── Annotation colors (cycle through a palette) ───────────────────────────────
const ANNOT_COLORS = [
  '#FF1744', '#39FF14', '#00BFFF', '#FFD700', '#FF9800',
  '#E91E63', '#00E5FF', '#76FF03', '#FF6D00', '#D500F9',
];
function annotColor(idx: number) { return ANNOT_COLORS[idx % ANNOT_COLORS.length]; }

// ── Annotated photo viewer ────────────────────────────────────────────────────
function AnnotatedPhoto({
  media,
  annotIndex,
  activeAnnot,
  setActiveAnnot,
}: {
  media: RefMedia;
  annotIndex: Map<string, number>; // media-scoped annotation index (annotation id → display number)
  activeAnnot: string | null;
  setActiveAnnot: (id: string | null) => void;
}) {
  const annotations = [...(media.repair_reference_annotations ?? [])].sort(
    (a, b) => a.sort_order - b.sort_order,
  );

  return (
    <div className="relative w-full">
      <div className="relative overflow-hidden rounded-2xl bg-gray-900 border border-gray-800">
        <img
          src={media.public_url}
          alt={media.caption || 'Board reference photo'}
          className="w-full h-auto block"
          style={{ maxHeight: '560px', objectFit: 'contain' }}
        />

        {/* Annotation dots */}
        {annotations.map((a, i) => {
          const num  = annotIndex.get(a.id ?? '') ?? i + 1;
          const col  = annotColor(num - 1);
          const isActive = activeAnnot === a.id;

          return (
            <button
              key={a.id ?? i}
              onClick={() => setActiveAnnot(isActive ? null : (a.id ?? null))}
              className="absolute -translate-x-1/2 -translate-y-1/2 transition-transform hover:scale-125 focus:outline-none z-10"
              style={{ left: `${a.x_percent}%`, top: `${a.y_percent}%` }}
              title={`${a.label}${a.measured_value ? ` — ${a.measured_value}${a.units ? ' ' + a.units : ''}` : ''}`}
            >
              <span
                className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-black border-2 shadow-lg transition-all ${isActive ? 'scale-125 ring-2 ring-white' : ''}`}
                style={{
                  backgroundColor: col,
                  borderColor: 'rgba(0,0,0,0.6)',
                  color: '#000',
                  boxShadow: `0 2px 8px ${col}80`,
                }}
              >
                {num}
              </span>
            </button>
          );
        })}
      </div>

      {media.caption && (
        <p className="text-xs text-gray-500 mt-2 text-center">{media.caption}</p>
      )}

      {/* Annotation detail popup */}
      {activeAnnot && (() => {
        const a = annotations.find(x => x.id === activeAnnot);
        if (!a) return null;
        const num = annotIndex.get(a.id ?? '') ?? 1;
        const col = annotColor(num - 1);
        return (
          <div className="mt-3 bg-gray-900 border border-gray-700 rounded-xl p-4 flex gap-3">
            <span
              className="shrink-0 flex items-center justify-center w-7 h-7 rounded-full text-xs font-black"
              style={{ backgroundColor: col, color: '#000' }}
            >
              {num}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[#E0E0E0] font-semibold text-sm">{a.label}</p>
              {a.measured_value && (
                <p className="text-[#39FF14] font-mono text-sm mt-0.5">
                  {a.measured_value}{a.units ? ' ' + a.units : ''}
                  {a.measurement_type ? <span className="text-[#B0B0B0] font-sans ml-2">({measurementTypeLabel(a.measurement_type)})</span> : null}
                </p>
              )}
              {a.notes && <p className="text-gray-400 text-xs mt-1 leading-relaxed">{a.notes}</p>}
            </div>
            <button onClick={() => setActiveAnnot(null)} className="text-gray-600 hover:text-gray-400 shrink-0 self-start">
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })()}

      {/* Annotation index table */}
      {annotations.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-900 border-b border-gray-800">
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#B0B0B0] w-8">#</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Point</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Reading</th>
                <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#B0B0B0] hidden md:table-cell">Notes</th>
              </tr>
            </thead>
            <tbody>
              {annotations.map((a, i) => {
                const num = annotIndex.get(a.id ?? '') ?? i + 1;
                const col = annotColor(num - 1);
                const isActive = activeAnnot === a.id;
                return (
                  <tr
                    key={a.id ?? i}
                    onClick={() => setActiveAnnot(isActive ? null : (a.id ?? null))}
                    className={`border-b border-gray-800/60 last:border-0 cursor-pointer transition-colors ${isActive ? 'bg-gray-800' : 'hover:bg-gray-900/60'}`}
                  >
                    <td className="px-4 py-2.5">
                      <span
                        className="flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black"
                        style={{ backgroundColor: col, color: '#000' }}
                      >
                        {num}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-[#E0E0E0] font-medium">{a.label}</td>
                    <td className="px-4 py-2.5 font-mono">
                      {a.measured_value
                        ? <span className="text-[#39FF14]">{a.measured_value}{a.units ? ' ' + a.units : ''}</span>
                        : <span className="text-gray-600">—</span>
                      }
                    </td>
                    <td className="px-4 py-2.5 text-gray-400 text-xs hidden md:table-cell">{a.notes || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Measurements table ────────────────────────────────────────────────────────
function MeasurementsTable({ measurements }: { measurements: RefMeasurement[] }) {
  if (!measurements.length) return null;
  const sorted = [...measurements].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="overflow-hidden rounded-xl border border-gray-800">
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3">
        <h3 className="text-sm font-bold uppercase tracking-widest text-[#B0B0B0]">Reference Measurements</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-900/50 border-b border-gray-800">
            <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Test Point</th>
            <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#B0B0B0] hidden sm:table-cell">Type</th>
            <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Value</th>
            <th className="text-left px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-[#B0B0B0] hidden md:table-cell">Notes</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((m, i) => (
            <tr key={m.id ?? i} className={`border-b border-gray-800/60 last:border-0 ${i % 2 === 0 ? 'bg-[#0D0D0D]' : 'bg-gray-900/20'}`}>
              <td className="px-4 py-3 text-[#E0E0E0] font-medium font-mono text-sm">{m.label}</td>
              <td className="px-4 py-3 text-gray-400 text-xs hidden sm:table-cell">
                {m.measurement_type ? measurementTypeLabel(m.measurement_type) : '—'}
              </td>
              <td className="px-4 py-3 font-mono">
                {m.measured_value
                  ? <span className="text-[#39FF14] font-semibold">{m.measured_value}{m.units ? ' ' + m.units : ''}</span>
                  : <span className="text-gray-600">—</span>
                }
              </td>
              <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">{m.notes || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={onClose}>
      <img src={src} alt={alt} className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" onClick={e => e.stopPropagation()} />
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white p-2">
        <X className="h-6 w-6" />
      </button>
    </div>
  );
}

// ── Linked repair cases ───────────────────────────────────────────────────────

function resultColor(result: string) {
  switch (result) {
    case 'repaired':           return 'text-green-400 border-green-800 bg-green-900/20';
    case 'partially-repaired': return 'text-yellow-400 border-yellow-800 bg-yellow-900/20';
    case 'no-fix':             return 'text-red-400 border-red-800 bg-red-900/20';
    default: return 'text-gray-400 border-gray-700 bg-gray-800/40';
  }
}

function LinkedCasesSection({ refId }: { refId: string }) {
  const [cases, setCases] = useState<LinkedCase[] | null>(null);

  useEffect(() => {
    fetchLinkedCases(refId)
      .then(setCases)
      .catch(() => setCases([]));
  }, [refId]);

  if (!cases || cases.length === 0) return null;

  return (
    <div>
      <h2 className="text-sm font-bold uppercase tracking-widest text-[#B0B0B0] mb-4 flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF1744] shrink-0" />
        Related Repair Cases
      </h2>
      <div className="space-y-3">
        {cases.map(c => {
          const resultLabel = RESULT_OPTIONS.find(r => r.value === c.result)?.label ?? c.result;
          return (
            <Link
              key={c.id}
              to={`/repair-database/${c.slug}`}
              className="flex gap-3 bg-gray-900 border border-[#FF1744]/20 hover:border-[#FF1744]/60 rounded-xl p-3 transition-all group"
            >
              {c.featured_image_url && (
                <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-gray-800 border border-gray-700">
                  <img src={c.featured_image_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#FF1744] mb-0.5">Repair Case</p>
                <p className="text-sm font-semibold text-[#E0E0E0] leading-snug truncate">{c.title || `${c.device_family} Repair`}</p>
                {(c.device_model || c.board_revision) && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {[c.device_model, c.board_revision].filter(Boolean).join(' · ')}
                  </p>
                )}
                {c.result && (
                  <span className={`inline-flex mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${resultColor(c.result)}`}>
                    {resultLabel}
                  </span>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-gray-600 group-hover:text-[#FF1744] shrink-0 self-center transition-colors" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function RepairReferencePage() {
  const { refId } = useParams<{ refId: string }>();
  const [ref, setRef]         = useState<RepairReference | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeAnnot, setActiveAnnot] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    if (!refId) return;
    setLoading(true);
    fetchRepairReference(refId)
      .then(data => { setRef(data); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [refId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <Loader className="h-8 w-8 text-[#39FF14] animate-spin" />
      </div>
    );
  }

  if (notFound || !ref) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex flex-col items-center justify-center gap-4 px-4">
        <AlertTriangle className="h-12 w-12 text-yellow-500" />
        <h1 className="text-2xl font-bold text-[#E0E0E0]">Reference not found</h1>
        <p className="text-gray-400">This reference may not exist or hasn't been published yet.</p>
        <Link to="/repair-database" className="text-[#39FF14] hover:underline text-sm">← Back to Repair Database</Link>
      </div>
    );
  }

  // Build a flat annotation index across all photos for consistent numbering within each photo
  const media = [...(ref.repair_reference_media ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const measurements = [...(ref.repair_reference_measurements ?? [])].sort((a, b) => a.sort_order - b.sort_order);

  // Per-photo annotation index map (annotation.id → 1-based number within that photo)
  const perPhotoIndex = new Map<string, Map<string, number>>();
  for (const m of media) {
    const photoMap = new Map<string, number>();
    const sorted = [...(m.repair_reference_annotations ?? [])].sort((a, b) => a.sort_order - b.sort_order);
    sorted.forEach((a, i) => { if (a.id) photoMap.set(a.id, i + 1); });
    if (m.id) perPhotoIndex.set(m.id, photoMap);
  }

  const deviceParts = [
    ref.device_family ? deviceLabel(ref.device_family) : '',
    ref.device_model,
    ref.board_revision,
  ].filter(Boolean);

  const hasAnnotations = media.some(m => (m.repair_reference_annotations?.length ?? 0) > 0);

  const canonical = `https://gamefixsolutions.com/repair-database/ref/${ref.slug || ref.id}`;

  return (
    <div className="bg-[#0D0D0D] min-h-screen">
      <SEOHead
        title={ref.seo_title || `${ref.title} | GameFix Board Reference`}
        description={ref.seo_description || `${ref.measurement_type ? measurementTypeLabel(ref.measurement_type) + ' for ' : 'Known-good board reference for '}${deviceParts.join(' ')}. ${ref.notes?.slice(0, 120) || ''}`}
        canonical={canonical}
        noIndex={ref.status !== 'published'}
      />

      {lightbox && (
        <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}

      {/* ── Hero / header ──────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-b from-black to-[#0D0D0D] border-b border-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-6">
            <Link to="/repair-database" className="hover:text-[#39FF14] transition-colors">Repair Database</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-gray-400">Board Reference</span>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#FF1744]/20 border border-[#FF1744]/40 text-[#FF1744] uppercase tracking-wide">
              Board Reference
            </span>
            {ref.category && (
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-300 uppercase tracking-wide">
                {categoryLabel(ref.category)}
              </span>
            )}
            {ref.measurement_type && (
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#00BFFF]/10 border border-[#00BFFF]/30 text-[#00BFFF] uppercase tracking-wide">
                {measurementTypeLabel(ref.measurement_type)}
              </span>
            )}
            {ref.verification_status && (
              <span className={`text-xs font-bold px-3 py-1 rounded-full border uppercase tracking-wide ${verificationColor(ref.verification_status)}`}>
                {verificationLabel(ref.verification_status)}
              </span>
            )}
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-[#E0E0E0] mb-3 leading-tight">{ref.title}</h1>

          {/* Device info */}
          {deviceParts.length > 0 && (
            <p className="text-[#B0B0B0] text-lg font-medium">
              {deviceParts.join(' · ')}
            </p>
          )}

          {/* Meta row */}
          <div className="flex flex-wrap gap-4 mt-4 text-xs text-gray-500">
            {ref.source_type && (
              <span>Source: <span className="text-gray-400">{sourceLabel(ref.source_type)}</span></span>
            )}
            {ref.confirmed_board_count > 0 && (
              <span>Confirmed on <span className="text-gray-400">{ref.confirmed_board_count} board{ref.confirmed_board_count !== 1 ? 's' : ''}</span></span>
            )}
            {ref.published_at && (
              <span>Published <span className="text-gray-400">{formatDate(ref.published_at)}</span></span>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* ── Main column ─────────────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-10">

            {/* Photos */}
            {media.length > 0 && (
              <div className="space-y-8">
                <div className="flex items-center gap-3">
                  <Image className="h-4 w-4 text-[#FF1744]" />
                  <h2 className="text-sm font-bold uppercase tracking-widest text-[#B0B0B0]">
                    Board Photos{hasAnnotations ? ' & Annotations' : ''}
                  </h2>
                </div>

                {hasAnnotations && (
                  <p className="text-xs text-gray-500 -mt-4">
                    Click any numbered marker on a photo to see the measurement detail.
                  </p>
                )}

                {media.map(m => (
                  <div key={m.id}>
                    <AnnotatedPhoto
                      media={m}
                      annotIndex={perPhotoIndex.get(m.id ?? '') ?? new Map()}
                      activeAnnot={activeAnnot}
                      setActiveAnnot={setActiveAnnot}
                    />
                    {/* Tap-to-expand lightbox trigger */}
                    <button
                      onClick={() => setLightbox({ src: m.public_url, alt: m.caption || ref.title })}
                      className="mt-2 text-xs text-gray-600 hover:text-gray-400 transition-colors block"
                    >
                      View full size ↗
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Measurements table */}
            {measurements.length > 0 && (
              <div>
                <MeasurementsTable measurements={measurements} />
              </div>
            )}

            {/* Notes */}
            {ref.notes && (
              <div>
                <h2 className="text-sm font-bold uppercase tracking-widest text-[#B0B0B0] mb-4">Notes</h2>
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                  {prose(ref.notes)}
                </div>
              </div>
            )}

            {/* Testing conditions */}
            {ref.testing_conditions && (
              <div>
                <h2 className="text-sm font-bold uppercase tracking-widest text-[#B0B0B0] mb-4">Testing Conditions</h2>
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                  {prose(ref.testing_conditions)}
                </div>
              </div>
            )}

            {/* No content fallback */}
            {media.length === 0 && measurements.length === 0 && !ref.notes && (
              <div className="text-center py-12 text-gray-600">
                <Image className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p>No media or measurements have been added to this reference yet.</p>
              </div>
            )}

            {/* Linked repair cases */}
            <LinkedCasesSection refId={ref.id} />
          </div>

          {/* ── Sidebar ─────────────────────────────────────────────────── */}
          <div className="space-y-5">

            {/* Quick-reference card */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-4 sticky top-6">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#B0B0B0]">Reference Details</h3>

              {deviceParts.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-0.5">Device</p>
                  <p className="text-sm text-[#E0E0E0] font-medium">{deviceParts.join(' · ')}</p>
                </div>
              )}

              {ref.category && (
                <div>
                  <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-0.5">Category</p>
                  <p className="text-sm text-[#E0E0E0]">{categoryLabel(ref.category)}</p>
                </div>
              )}

              {ref.measurement_type && (
                <div>
                  <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-0.5">Measurement Type</p>
                  <p className="text-sm text-[#E0E0E0]">{measurementTypeLabel(ref.measurement_type)}</p>
                </div>
              )}

              {ref.verification_status && (
                <div>
                  <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-0.5">Verification</p>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${verificationColor(ref.verification_status)}`}>
                    {verificationLabel(ref.verification_status)}
                  </span>
                  <p className="text-xs text-gray-600 mt-1">
                    {REF_VERIFICATION.find(r => r.value === ref.verification_status)?.description}
                  </p>
                </div>
              )}

              {ref.confirmed_board_count > 0 && (
                <div>
                  <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-0.5">Confirmed On</p>
                  <p className="text-sm text-[#E0E0E0]">{ref.confirmed_board_count} board{ref.confirmed_board_count !== 1 ? 's' : ''}</p>
                </div>
              )}

              {ref.source_type && (
                <div>
                  <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-0.5">Source</p>
                  <p className="text-sm text-[#E0E0E0]">{sourceLabel(ref.source_type)}</p>
                  {ref.source_url && (
                    <a
                      href={ref.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#00BFFF] hover:underline mt-0.5 block truncate"
                    >
                      {ref.source_url}
                    </a>
                  )}
                </div>
              )}

              {ref.published_at && (
                <div>
                  <p className="text-[10px] text-gray-600 uppercase tracking-wide mb-0.5">Published</p>
                  <p className="text-sm text-gray-400">{formatDate(ref.published_at)}</p>
                </div>
              )}

              {/* Stats */}
              {(media.length > 0 || measurements.length > 0) && (
                <div className="border-t border-gray-800 pt-4 grid grid-cols-2 gap-3 text-center">
                  {media.length > 0 && (
                    <div>
                      <p className="text-xl font-bold text-[#FF1744]">{media.length}</p>
                      <p className="text-[10px] text-gray-600 uppercase tracking-wide">Photo{media.length !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                  {measurements.length > 0 && (
                    <div>
                      <p className="text-xl font-bold text-[#39FF14]">{measurements.length}</p>
                      <p className="text-[10px] text-gray-600 uppercase tracking-wide">Measurement{measurements.length !== 1 ? 's' : ''}</p>
                    </div>
                  )}
                  {hasAnnotations && (() => {
                    const total = media.reduce((n, m) => n + (m.repair_reference_annotations?.length ?? 0), 0);
                    return (
                      <div>
                        <p className="text-xl font-bold text-[#00BFFF]">{total}</p>
                        <p className="text-[10px] text-gray-600 uppercase tracking-wide">Annotation{total !== 1 ? 's' : ''}</p>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Tags */}
            {ref.tags?.length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
                <h3 className="text-xs font-bold uppercase tracking-widest text-[#B0B0B0] mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {ref.tags.map(tag => (
                    <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-gray-800 border border-gray-700 text-gray-300">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Back link */}
            <Link
              to="/repair-database"
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#39FF14] transition-colors"
            >
              ← Back to Repair Database
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
